import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import {LightButtonComponent} from "../LightButton/light-button.component";

import {MealKitCalendarComponent} from "@yoni/mealcomponents-lib";

@Component({
    imports: [
    LightButtonComponent,
    MealKitCalendarComponent
],
    selector: "date-selection-header",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "(window:click)": "onWindowClick($event)",
    },
    template: `
    <div
      class="w-full rounded bg-vert-1 text-white font-semibold flex items-center px-2 relative rounded-b-none select-none">
      <div class="mx-auto flex items-center gap-2 py-2 md:py-4">
        @if (this.mode() === 'day') {
          <light-button (click)="this.previousDay()" class="w-8 h-8" iconName="chevron_left"/>
          <span (click)="this.toggleCalendarVisibility()"
            [innerHTML]="this.formattedDate()"
          class="calendar-opener h-full w-64 max-w-64 overflow-hidden cursor-pointer text-center select-text"></span>
          <light-button (click)="this.nextDay()" class="w-8 h-8 mr-8" iconName="chevron_right"/>
        }
    
        @if (this.mode() === 'week') {
          <light-button (click)="this.previousDay()" class="w-8 h-8" iconName="chevron_left"/>
          <span (click)="this.toggleCalendarVisibility()"
            [innerHTML]="this.formattedDate()"
          class="calendar-opener h-full w-64 max-w-64 overflow-hidden cursor-pointer text-center select-text"></span>
          <light-button (click)="this.nextDay()" class="w-8 h-8 mr-8" iconName="chevron_right"/>
        }
      </div>
    
      <div class="absolute right-1/2 md:right-0 top-1/2 -translate-y-1/2 translate-x-[260%] md:-translate-x-1/2 calendar-toggle-button">
        <light-button (click)="this.toggleCalendarVisibility()" class="w-8 h-8" iconName="calendar_month"/>
      </div>
    
      @if (!isCalendarHidden()) {
        <div class="fixed left-0 top-0 right-0 bottom-0 bg-black/50 z-[5] block md:hidden"></div>
      }
      @if (!isCalendarHidden()) {
        <div
          class="absolute top-0 md:top-20 right-6 z-10 text-black w-80 md:w-96 border border-gris-3 rounded overflow-hidden "
          >
          <mk-new-calendar
            [daysWithMenus]="this.daysWithMenus()"
            [dayConsultationLimit]="this.dayConsultationLimit()"
            [daysWithOrders]="this.daysWithFullOrders()"
            [daysWithPartialOrder]="this.daysWithPartialsOrders()"
            [startDate]="this.currentDate() || this.today"
            [selectionMode]="this.mode() === 'day' ? 'perDay' : 'perWeek'"
            (onDateChange)="this.onDateChange($event)"/>
          </div>
        }
      </div>
    `
})
export class DateSelectionHeader {

  readonly mode = input.required<"day" | "week">();

  readonly daysWithMenus = input.required<Date[]>();

  readonly daysWithOrders = input.required<Date[]>();

  readonly currentDate = input.required<Date | undefined>();

  readonly dayConsultationLimit = input.required<{
    future: number;
    past: number;
  }>();

  readonly daysWithPartialsOrders = input.required<Date[]>();

  readonly daysWithFullOrders = input.required<Date[]>();

  readonly dateChangedWithArrows = output<number>();

  readonly dateChangedExact = output<Date>();

  protected readonly formattedDate = computed(
    () => this.currentDate()?.formatDate(this.mode() === "day" ? "day" : "week") ?? ""
  );

  readonly today: Date = new Date();

  protected readonly isCalendarHidden = signal(true);

  onWindowClick(event: MouseEvent){
    const target = event.target as HTMLElement;

    // Vérifier si l'élément cliqué est le bouton calendrier, le span date ou le calendrier
    if (
      target.closest(".calendar-opener") ||
      target.closest(".calendar-toggle-button") ||
      target.closest(".month-picker") ||
      target.closest("mk-new-calendar")
    ) {
      return;
    }

    this.isCalendarHidden.set(true);
  }

  previousDay() {
    this.dateChangedWithArrows.emit(this.mode() === "day" ? -1 : -7);
  }

  nextDay() {
    this.dateChangedWithArrows.emit(this.mode() === "day" ? 1 : 7);
  }

  toggleCalendarVisibility() {
    this.isCalendarHidden.update((hidden) => !hidden);
  }

  onDateChange(date: Date) {
    this.dateChangedExact.emit(date);
  }
}
